import { Box, BoxProps, Flex } from '@chakra-ui/react'
import React, {
  ForwardedRef,
  MutableRefObject,
  RefCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import type { ComponentType } from 'react'

export interface ScrollbarOptions {
  hidden?: boolean
  showOnHoverOnly?: boolean
}

export type ScrollAreaProps = BoxProps & {
  scrollbarOptions?: ScrollbarOptions
  scrollTop?: number
}

type UseCustomScrollerProps = ScrollAreaProps & {
  deps?: unknown[]
}
export const useScrollingListScrollArea = (props: UseCustomScrollerProps) => {
  const scrollTopRef = useRef(0)
  const handleOnScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop
    scrollTopRef.current = scrollTop
  }

  const { scrollbarOptions, deps, ...rest } = props
  const scroller = useMemo(() => {
    return React.forwardRef((props: ScrollAreaProps, ref) => (
      <ScrollArea
        onScroll={handleOnScroll}
        ref={ref as RefCallback<HTMLDivElement>}
        scrollTop={scrollTopRef.current}
        scrollbarOptions={scrollbarOptions}
        {...rest}
        {...props}
      />
    )) as ComponentType<ScrollAreaProps>
  }, [deps])

  return scroller
}

/**
 * @description A custom scroller for ScrollingList and GroupedScrollingList.  Not intended to be used outside of these components.
 */
export const ScrollArea = React.forwardRef<
  HTMLDivElement,
  BoxProps & ScrollAreaProps
>((props: ScrollAreaProps, ref: ForwardedRef<HTMLDivElement>) => {
  const { scrollbarOptions = {}, scrollTop, ...rest } = props
  const internalRef = useRef<HTMLDivElement>(null)
  const refObj = (ref as MutableRefObject<HTMLDivElement>) || internalRef

  useEffect(() => {
    if (!refObj.current || !scrollTop) {
      return
    }

    refObj.current.scrollTop = scrollTop
  }, [scrollTop])

  const className = scrollbarOptions.showOnHoverOnly
    ? 'scrollbar-show-on-hover'
    : scrollbarOptions.hidden
      ? 'scrollbar-hidden'
      : ''

  return (
    <Box
      ref={ref}
      className={className}
      {...rest}
      overflowY="scroll"
      overflowX="hidden"
      position="relative"
    >
      <Flex
        direction="column"
        flex={1}
        w="calc(100% - 6px)"
        position="relative"
      >
        {props.children}
      </Flex>
    </Box>
  )
})
